<template>
  <page-template :is-loading="isLoading" :data-exists="flowControlPolicies !== null">
    <template #pageHeadline>
      {{ $t('flowControlPolicy.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="flowControlPolicies"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="flow-control-policies-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewFlowControlPolicyPath()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateFlowControlPolicy"
        @delete-row-button-clicked="openDeleteFlowControlPolicyModal"
        @download-data="downloadDoc"
      >
      </rbz-table>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="loadFlowControlPolicies"
      />
      <rbz-modal-dialog
        :show="showDeleteFlowControlPolicyModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteFlowControlPolicy"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteFlowControlPolicyModal = false"
        @closeModal="showDeleteFlowControlPolicyModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', { name: selectedFlowControlPolicy.name })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { DocumentName, FlowControlPolicy } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import GitHistory from '@/components/GitHistory.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'FlowControlPoliciesList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    GitHistory,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('flowControlPolicy.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getFlowControlPolicyPath(item);
          },
        },
        {
          id: 'active',
          title: this.$t('flowControlPolicy.listPage.active'),
          fieldNames: ['active'],
          displayFunction: (item: FlowControlPolicy) => {
            return item.active ? this.$t('common.yes') : this.$t('common.no');
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-100px',
        },
        {
          id: 'steps',
          title: this.$t('flowControlPolicy.listPage.steps'),
          fieldNames: ['steps'],
          displayFunction: (item: FlowControlPolicy) => {
            return item.steps?.length || 0;
          },
          isSortable: true,
          isSearchable: true,
          isNumber: true,
          classes: 'width-160px',
        },
        {
          id: 'time-frame',
          title: this.$t('flowControlPolicy.listPage.timeFrame'),
          fieldNames: ['timeframe'],
          isSortable: true,
          isSearchable: true,
          isNumber: true,
          classes: 'width-160px',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getFlowControlPolicyPath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      flowControlPolicies: null as FlowControlPolicy[],
      pageLoadingCounter: null,

      showDeleteFlowControlPolicyModal: false,
      selectedFlowControlPolicy: null,
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if (
          (this.$route.name as string).includes('Flow Control Policies List') &&
          val &&
          val !== oldVal
        ) {
          this.setPageLoadingStatus(true);
          await this.loadFlowControlPolicies();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('flowControlPolicy.plural'),
        singular: this.$t('flowControlPolicy.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/flow-control-policies/versions`;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadFlowControlPolicies() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/flow-control-policies`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      this.flowControlPolicies = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    isDeleteDisabled(flowControlPolicy: FlowControlPolicy) {
      return this.isFlowControlPolicyProtected(flowControlPolicy);
    },

    isFlowControlPolicyProtected(flowControlPolicy: FlowControlPolicy): boolean {
      return flowControlPolicy.id.startsWith('__');
    },

    deleteTooltip(flowControlPolicy: FlowControlPolicy): string {
      if (this.isFlowControlPolicyProtected(flowControlPolicy)) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    getFlowControlPolicyPath(flowControlPolicy: FlowControlPolicy) {
      return this.getEditPath('flow-control-policies', flowControlPolicy.id);
    },

    editFlowControlPolicy(flowControlPolicy: FlowControlPolicy) {
      this.navigateTo(this.getFlowControlPolicyPath(flowControlPolicy));
    },

    async duplicateFlowControlPolicy(flowControlPolicy: FlowControlPolicy) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/flow-control-policies/${flowControlPolicy.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalFlowControlPolicy = getOriginalResponse.data;
      const flowControlPolicyToAdd = _.cloneDeep(originalFlowControlPolicy);
      flowControlPolicyToAdd.id = DatasetsUtils.generateUUID2();
      flowControlPolicyToAdd.name = Utils.generateUniqueEntityName(
        flowControlPolicyToAdd.name,
        _.map(this.flowControlPolicies, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/flow-control-policies/${flowControlPolicyToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: flowControlPolicyToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editFlowControlPolicy(flowControlPolicyToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteFlowControlPolicy() {
      this.setPageLoadingStatus(true);
      const flowControlPolicyId = this.selectedFlowControlPolicy.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/flow-control-policies/${flowControlPolicyId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.flowControlPolicies = _.filter(this.flowControlPolicies, (flowControlPolicy) => {
          return flowControlPolicy.id !== flowControlPolicyId;
        });
      }
      this.showDeleteFlowControlPolicyModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('flow-control-policies', 'json', this.flowControlPolicies);
    },

    openDeleteFlowControlPolicyModal(flowControlPolicy: FlowControlPolicy) {
      this.showDeleteFlowControlPolicyModal = true;
      this.selectedFlowControlPolicy = flowControlPolicy;
    },

    getNewFlowControlPolicyPath() {
      return this.getNewPath('flow-control-policies');
    },
  },
});
</script>
